import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarHeart, Check, Crown, Gift, Sparkles, Star } from "lucide-react";
import { HouseDialog } from "@/components/site/HouseDialog";

export const Bunny = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"
    strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M9.2 9.6C8.4 7.2 8.6 4.4 10 2.6c1 1.4 1.6 3.6 1.5 5.9" />
    <path d="M14.8 9.6c.8-2.4.6-5.2-.8-7-1 1.4-1.6 3.6-1.5 5.9" />
    <circle cx="12" cy="13.6" r="4.6" />
    <circle cx="10.4" cy="12.8" r=".4" fill="currentColor" />
    <circle cx="13.6" cy="12.8" r=".4" fill="currentColor" />
    <path d="M10.6 14.9c.9.7 1.9.7 2.8 0" />
    <path d="M8.2 19.5 12 18l3.8 1.5L12 21.2z" fill="currentColor" stroke="none" opacity=".9" />
  </svg>
);

const GOLD_TEXT = {
  background: "linear-gradient(100deg, #8a5a2b 0%, #edc48c 25%, #fff3dd 50%, #edc48c 75%, #8a5a2b 100%)",
  backgroundSize: "200% 100%",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const rise = (d = 0) => ({
  initial: { y: 26, opacity: 0.25 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, delay: d, ease: [0.16, 1, 0.3, 1] },
});

const JOURNEY = [
  ["I", "Your First Crown", "Book and complete your first appointment with LocdbySmiless."],
  ["II", "Return to the Lounge", "Come back and complete your second appointment."],
  ["III", "You've Qualified", "Membership is free — and it can't be bought. You earned it."],
  ["IV", "Receive Your Black Card", "Your second appointment counts as your first gold bunny stamp."],
];

const RULES = [
  "Has no cash value and can't be exchanged for real money",
  "Non-transferable — only you can redeem it",
  "Redeemable once, toward eligible LocdbySmiless services",
  "Can't be combined with other promotional discounts",
  "Verified by Smiless before redemption",
];

export function StampTracker({ stamps, testPrefix = "vip-stamp" }) {
  return (
    <div className="passport">
      {Array.from({ length: 10 }, (_, i) => {
        const filled = i < stamps;
        const milestone = { 2: 5, 4: 15, 9: 30 }[i];
        return (
          <div key={i} className="stamp-slot" data-filled={filled} data-testid={`${testPrefix}-${i + 1}`}>
            {filled ? (
              <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 13, delay: i * 0.05 }}>
                <Bunny className="stamp-bunny" />
              </motion.div>
            ) : (
              <Bunny className="stamp-bunny ghost" />
            )}
            {milestone ? <span className="stamp-reward">${milestone}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

function BlackCard({ name, memberNo, compact }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div className={`card-scene ${compact ? "compact" : ""}`} onClick={() => setFlipped(!flipped)}
      role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setFlipped(!flipped)}
      data-testid="vip-black-card">
      <motion.div className="black-card" animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} style={{ transformStyle: "preserve-3d" }}>
        <div className="bc-face bc-front">
          <div className="bc-top">
            <span className="bc-brand" style={GOLD_TEXT}>THE BUNNY BANK <em>VIP LOUNGE</em></span>
            <Bunny className="bc-bunny" />
          </div>
          <div className="bc-monogram" style={GOLD_TEXT}>LS</div>
          <div className="bc-bottom">
            <span className="bc-tier" style={GOLD_TEXT}>BLACK CARD MEMBER</span>
            <span className="bc-house" style={GOLD_TEXT}>LOCDBYSMILESS</span>
          </div>
        </div>
        <div className="bc-face bc-back">
          <div className="bc-strip" />
          <div className="bc-num" style={GOLD_TEXT}>{memberNo || "•••• ••••"}</div>
          <div className="bc-name">{name || "MEMBER NAME"}</div>
          <div className="bc-small" style={GOLD_TEXT}>MEMBERSHIP CAN'T BE BOUGHT. LOYALTY GETS YOU CROWNED.</div>
          {name ? null : <p className="bc-hint">Sign in below to put your name on the card.</p>}
        </div>
      </motion.div>
    </div>
  );
}

const VOUCHERS = [
  { v: 5, when: "3 stamps" }, { v: 15, when: "5 stamps" }, { v: 30, when: "10 stamps + renewed Black Card" },
];

export default function VipPage() {
  const [token, setToken] = useState(() => localStorage.getItem("lbs_vip_token") || "");
  const [me, setMe] = useState(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [checked, setChecked] = useState(!!token);

  const signIn = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const { api } = await import("@/lib/api");
      const r = await api.post("/vip/login", { email, code });
      localStorage.setItem("lbs_vip_token", r.data.token);
      setToken(r.data.token);
      const me2 = await api.get("/vip/me");
      setMe(me2.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail === undefined ? "No membership found for that email and access code."
        : (typeof e2.response.data.detail === "string" ? e2.response.data.detail : "Could not sign in."));
    } finally { setBusy(false); }
  };

  if (token && !me && checked) {
    import("@/lib/api").then(({ api }) => api.get("/vip/me")
      .then((r) => setMe(r.data)).catch(() => { localStorage.removeItem("lbs_vip_token"); setToken(""); })
      .finally(() => setChecked(false)));
  }
  const signOut = () => { localStorage.removeItem("lbs_vip_token"); setToken(""); setMe(null); setChecked(false); };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const kashAvailable = (me?.kash || []).filter((k) => k.status === "issued");
  const thisYear = new Date().getFullYear();
  const birthdayAvailable = me && me.birthday_month === new Date().getMonth() + 1
    && !(me.birthday_used || []).includes(thisYear);
  const stamps = me ? me.stamps_cycle : 0;

  return (
    <div className="vip-page" data-testid="vip-page">
      <section className="vip-hero">
        <motion.p className="vip-eyebrow" {...rise()}><Sparkles size={14} /> AN INVITATION FROM THE HOUSE OF SMILESS <Sparkles size={14} /></motion.p>
        <motion.h1 className="vip-title" {...rise(0.08)}>
          THE BUNNY BANK<br /><span className="vip-title-gold">VIP LOUNGE</span>
        </motion.h1>
        <motion.p className="vip-tagline" {...rise(0.16)}>Membership Can't Be Bought. Loyalty Gets You Crowned.</motion.p>
        <motion.p className="vip-sub" {...rise(0.22)}>"Where loyalty is luxury, and every crown earns privileges."</motion.p>
        <motion.div className="vip-ctas" {...rise(0.3)}>
          <button type="button" className="cta" onClick={() => scrollTo("vip-journey")} data-testid="vip-cta-discover">Discover Your Privileges</button>
          <button type="button" className="cta ghost" onClick={() => scrollTo("vip-card-section")} data-testid="vip-cta-card">Explore the Black Card</button>
          <button type="button" className="cta ghost" onClick={() => scrollTo("vip-member")} data-testid="vip-cta-rewards">View My Rewards</button>
          <Link to="/booking" className="cta gold" data-testid="vip-cta-book">Book Your Next Crown Appointment</Link>
        </motion.div>
      </section>

      <section className="vip-section" id="vip-journey">
        <motion.h2 className="vip-h2" {...rise()}>The VIP Journey</motion.h2>
        <motion.p className="vip-lead" {...rise(0.05)}>
          An exclusive lounge for returning LocdbySmiless clients. Membership is completely free — and cannot be purchased.
        </motion.p>
        <div className="journey">
          {JOURNEY.map(([num, t, d], i) => (
            <motion.div className="journey-card" key={t} {...rise(i * 0.07)} data-testid={`vip-journey-${i + 1}`}>
              <span className="journey-num" style={GOLD_TEXT}>{num}</span>
              <b>{t}</b><p>{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="vip-section" id="vip-card-section">
        <motion.h2 className="vip-h2" {...rise()}>The Exclusive Black Card</motion.h2>
        <motion.p className="vip-lead" {...rise(0.05)}>Matte black. Gold foil. Yours once you qualify — tap the card to turn it over.</motion.p>
        <motion.div {...rise(0.1)} className="vip-card-wrap">
          <BlackCard name={me?.name} memberNo={me?.member_no} />
        </motion.div>
      </section>

      <section className="vip-section" id="vip-stamps">
        <motion.h2 className="vip-h2" {...rise()}>Gold Bunny Stamps</motion.h2>
        <motion.p className="vip-lead" {...rise(0.05)}>
          One gold bunny stamp for every completed appointment of $55 or more. Rewards unlock at 3, 5 and 10 — then your card renews and the chase begins again.
        </motion.p>
        <motion.div {...rise(0.1)}>
          <StampTracker stamps={stamps} />
          <div className="milestones">
            <span><Star size={13} /> 3 stamps — $5 Smiless Kash</span>
            <span><Star size={13} /> 5 stamps — $15 Smiless Kash</span>
            <span><Star size={13} /> 10 stamps — $30 Smiless Kash + renewed Black Card</span>
          </div>
        </motion.div>
      </section>

      <section className="vip-section" id="vip-kash">
        <motion.h2 className="vip-h2" {...rise()}>Smiless Kash</motion.h2>
        <motion.p className="vip-tagline2" {...rise(0.05)}>"Your loyalty pays at the House."</motion.p>
        <motion.p className="vip-lead" {...rise(0.08)}>
          Promotional credit you earn in the Lounge and redeem toward future eligible LocdbySmiless services.
        </motion.p>
        <div className="kash-row">
          {VOUCHERS.map((v, i) => (
            <motion.div className={`kash-voucher kv${v.v}`} key={v.v} {...rise(i * 0.08)} data-testid={`vip-kash-${v.v}`}>
              <span className="kv-ls" style={GOLD_TEXT}>LS</span>
              <Bunny className="kv-bunny" />
              <span className="kv-value" style={GOLD_TEXT}>${v.v}</span>
              <span className="kv-when">earned at {v.when}</span>
              <span className="kv-fine">Issued by the House of Smiless</span>
              <span className="kv-fine">VIP REWARD CREDIT — NOT LEGAL TENDER</span>
            </motion.div>
          ))}
        </div>
        <motion.button type="button" className="cta ghost sm" {...rise(0.15)} onClick={() => setRulesOpen(true)}
          data-testid="vip-kash-rules-button">Read the Kash rules</motion.button>
      </section>

      <section className="vip-section" id="vip-birthday">
        <motion.div className="birthday-card" {...rise()}>
          <p className="vip-eyebrow"><Gift size={14} /> A GIFT FROM THE HOUSE OF SMILESS</p>
          <h2 className="vip-h2">Your Birthday. Your Crown. Your Privilege.</h2>
          <p className="birthday-off" style={GOLD_TEXT}>$25 OFF one appointment during your birthday month</p>
          <ul className="birthday-terms">
            <li>Eligible VIP members only · once per calendar year</li>
            <li>One qualifying service appointment during your birthday month</li>
            <li>Can't be combined with Smiless Kash or other discounts, exchanged for cash, or transferred</li>
            <li>The normal $15 booking deposit still applies toward the discounted total</li>
          </ul>
        </motion.div>
      </section>

      <section className="vip-section member-section" id="vip-member">
        <motion.h2 className="vip-h2" {...rise()}>Member Access</motion.h2>
        {me ? (
          <motion.div className="member-dash" {...rise(0.05)} data-testid="vip-dashboard">
            <div className="dash-left">
              <BlackCard name={me.name} memberNo={me.member_no} compact />
              <p className="dash-line">Member since {new Date(me.joined_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
              <p className="dash-line">Lifetime gold bunny stamps: <b>{me.total_stamps}</b> · Card cycle <b>{me.cycle_number}</b></p>
              {birthdayAvailable ? <p className="dash-bday" data-testid="vip-birthday-available">🎂 It's your birthday month — your $25-off privilege is ready! Ask Smiless to apply it.</p>
                : <p className="dash-line">Birthday privilege: {me.birthday_month ? `December`.replace("December", ["January","February","March","April","May","June","July","August","September","October","November","December"][me.birthday_month - 1]) : "add your birth month with Smiless"}{me.birthday_month ? ` — ${(me.birthday_used || []).length ? "used in " + (me.birthday_used || []).join(", ") : "available once per year"}` : ""}</p>}
              <button type="button" className="cta ghost sm" onClick={signOut} data-testid="vip-signout">Sign out</button>
            </div>
            <div className="dash-right">
              <h3>Gold Bunny Stamps</h3>
              <StampTracker stamps={me.stamps_cycle} testPrefix="vip-my-stamp" />
              <h3>Smiless Kash</h3>
              {me.kash.length === 0 ? <p className="dash-line">No Kash yet — your first $5 unlocks at stamp 3.</p> : (
                <div className="my-kash">
                  {me.kash.map((k) => (
                    <div key={k.id} className={`my-kash-item ${k.status}`} data-testid={`vip-my-kash-${k.code}`}>
                      <b style={GOLD_TEXT}>${k.value}</b>
                      <span>{k.code}</span>
                      <em data-testid={`vip-kash-status-${k.code}`}>{k.status === "issued" ? "ready to use" : k.status}</em>
                    </div>
                  ))}
                </div>
              )}
              {me.ledger?.length ? (
                <details className="vip-history"><summary>Membership history</summary>
                  <ul>{me.ledger.map((l) => <li key={l.id}>{new Date(l.created_at).toLocaleDateString()} — {l.note}</li>)}</ul>
                </details>
              ) : null}
            </div>
          </motion.div>
        ) : (
          <motion.form className="vip-login" onSubmit={signIn} {...rise(0.05)} data-testid="vip-login-form">
            <p className="vip-lead">Not yet eligible? Membership unlocks after your <b>second completed appointment</b> — Smiless will invite you with your personal access code.</p>
            <div className="vip-login-row">
              <input className="st-control" type="email" required placeholder="Email" value={email}
                onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="vip-email-input" />
              <input className="st-control" required placeholder="Access code" value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="vip-code-input" />
            </div>
            {err ? <p className="form-err" data-testid="vip-login-error">{err}</p> : null}
            <button className="cta" disabled={busy} data-testid="vip-signin-button">{busy ? "Checking…" : "View My Rewards"}</button>
            <Link to="/booking" className="vip-book-link" data-testid="vip-book-link">
              <CalendarHeart size={16} /> Book your first (or next) crown appointment
            </Link>
          </motion.form>
        )}
      </section>

      <footer className="vip-footer">
        <Crown strokeWidth={1.2} />
        <p>THE BUNNY BANK VIP LOUNGE — MEMBERSHIP CAN'T BE BOUGHT. LOYALTY GETS YOU CROWNED. 🖤👑</p>
      </footer>

      <HouseDialog open={rulesOpen} onOpenChange={setRulesOpen} title="Smiless Kash Rules ♛"
        description="VIP reward credit — not legal tender." testId="vip-kash-rules-dialog">
        <ul className="kash-rules">
          {RULES.map((r, i) => <li key={i}><Check /> {r}</li>)}
        </ul>
      </HouseDialog>
    </div>
  );
}
